/**
 * Persona chat lines (shared contract, see types.ts): what bots say in party chat, per situation,
 * flavoured by archetype and tone. @keystone/bots picks and rate-limits them (persona/chat.ts).
 *
 * Shape: LINES[situation] is a pool with optional buckets:
 *   - `any`: anyone may say it;
 *   - `kind` / `salty`: tone, picked by the persona's toxicity (salty when toxicity > 0.5);
 *   - one bucket per archetype: that archetype's own voice (preferred when present).
 * Placeholders: {victim} (who died / messed up), {ability} (what killed them), {boss}, {n} (count),
 * {nth} ("3rd"), {human} (the human's name), {self} (the speaker), {target} (the mob to kick).
 * A line starting with '*' is an emote ("*dances*").
 *
 * Tone rule: playful salt only. No slurs, no real abuse, nothing about real people.
 */
import type { Archetype } from './types';

export type ChatSituation =
  | 'run_start'
  | 'greet_human'
  | 'pull'
  | 'big_pull'
  | 'boss_pull'
  | 'boss_kill'
  | 'other_died'
  | 'self_died'
  | 'human_died'
  | 'wipe'
  | 'kick_landed'
  | 'kick_missed'
  | 'interrupt_call'
  | 'loot'
  | 'low_timer'
  | 'timed'
  | 'depleted'
  | 'afk_start'
  | 'afk_long'
  | 'afk_back'
  | 'wander_start'
  | 'wander_back'
  | 'tilt_1'
  | 'tilt_2'
  | 'rage_quit'
  | 'someone_left'
  | 'abandon'
  | 'afk_kick'
  | 'ambient'
  | 'reply'
  | 'waiting';

export type LinePool = { readonly any?: readonly string[]; readonly kind?: readonly string[]; readonly salty?: readonly string[] } & {
  readonly [A in Archetype]?: readonly string[];
};

export const LINES: Readonly<Record<ChatSituation, LinePool>> = {
  run_start: {
    any: ['hi', 'hey all', 'o/', 'ready', 'lets go', 'gl hf', 'yo'],
    tryhard: ['route is standard, dont improvise', 'pls kick', 'no deaths pls', 'I have 20 min, lets time it', 'dont stand in stuff and we are fine'],
    chill: ['hey friends', 'chill run, no stress', 'vibes only'],
    newbie: ['hi! first time in here, sorry in advance', 'is this the one with the snakes', 'I watched a video on this one', 'hello!! :)'],
    goblin: ['anyone need herbs? I have herbs', 'nodes are mine btw', 'hi, I will be looting'],
    distracted: ['hey, might have to afk at some point', 'sup', 'hi (eating, sorry)'],
    clown: ['*waves*', 'hello fellow adventurers', 'who brought snacks', '*dances*', 'pumpers assemble'],
    silent: ['.', 'o/'],
    leader: ['hey all, I will call pulls', 'standard route, kicks on casters please', 'quick plan: big pulls on trash, careful on bosses'],
  },
  greet_human: {
    any: ['hi {human}', 'hey {human}', 'o/ {human}'],
    kind: ['welcome {human}!', 'glad to have you {human}', 'nice, a real person'],
    salty: ['{human} you better be good', 'ok {human}, dont make me regret this'],
    newbie: ['hi {human}! go easy on me lol'],
    leader: ['hey {human}, just follow me and kick casters'],
    clown: ['{human}! *salutes*'],
  },
  pull: {
    any: ['pulling', 'going', 'ok next', 'here we go'],
    tryhard: ['go go go', 'faster', 'chain it', 'no stopping'],
    leader: ['pulling, kicks ready', 'this one then next', 'save cds for the next one'],
    clown: ['LEEROY', 'I sense danger', 'charge!!'],
    newbie: ['oh no', 'ok ok ok'],
  },
  big_pull: {
    any: ['big pull!', 'thats a lot of mobs', 'oh boy', 'big one'],
    tryhard: ['send it', 'big pull, press everything', 'cds now'],
    chill: ['bit spicy', 'lol ok'],
    newbie: ['is that too many??', 'ahhh'],
    leader: ['big pull, defensives ready', 'pop cds here'],
    clown: ['we are so back', 'this is fine', 'mass pull of destiny'],
    silent: ['...'],
  },
  boss_pull: {
    any: ['boss', 'ready?', 'boss time'],
    tryhard: ['dont stand in the swirls', 'pls do mechanics', 'easy boss, dont mess it up'],
    leader: ['boss: spread for the bolts, stack for the soak', 'everyone ready? pulling boss'],
    newbie: ['what does this boss do', 'ok I read the tooltips I think'],
    clown: ['for the horde! wait wrong game', 'tell my cat I love her'],
    distracted: ['wait what boss is this', 'sec— ok go'],
  },
  boss_kill: {
    any: ['gj', 'nice', 'ez', 'down', 'gg boss'],
    kind: ['nice job everyone', 'well played'],
    salty: ['finally', 'took long enough'],
    tryhard: ['ok move', 'next', 'not bad'],
    newbie: ['we did it!!', 'yay!', 'that was so cool'],
    clown: ['*cheers*', 'another one bites the dust'],
    goblin: ['loot loot loot', 'any drops for me?'],
  },
  other_died: {
    any: ['rip {victim}', 'F', 'oof'],
    kind: ['np {victim}', 'its ok {victim}', 'happens', 'we got this'],
    salty: ['{victim}...', 'really {victim}?', '{victim} that was {ability}', 'cmon {victim}', 'thats {nth} death to {ability} {victim}', 'dodge pls', 'stop standing in stuff'],
    tryhard: ['{victim} you have to move for {ability}', 'that is the {nth} time, {victim}', 'please watch {ability}', 'it is not that hard to dodge'],
    newbie: ['oh no {victim}!', 'was that my fault?'],
    leader: ['{victim} watch {ability}, step out early', 'np, rez after'],
    clown: ['{victim} has left the chat', 'press F for {victim}', '{victim} achieved floor'],
    chill: ['lol rip', 'happens to the best of us'],
  },
  self_died: {
    any: ['oops', 'my bad', 'sorry', 'rip me'],
    kind: ['sorry guys', 'my fault'],
    salty: ['heals??', 'where were the heals', 'lag', 'that was not my fault', 'tank ran away'],
    tryhard: ['heals??', 'no externals?', 'that should not have killed me', 'healer?'],
    newbie: ['sorry!! I didnt see it', 'what killed me', 'I will dodge next time sorry'],
    clown: ['I meant to do that', 'floor tank reporting', 'taking a nap brb'],
    distracted: ['oops was looking at my phone', 'sorry had the door'],
    goblin: ['worth it'],
  },
  human_died: {
    any: ['rip {human}', 'oof {human}'],
    kind: ['np {human}', 'dont worry {human}', 'its fine {human}, we got it', 'np, happens'],
    salty: ['{human}...', '{human} that was {ability}', 'cmon {human}', 'dodge {ability} pls {human}', 'thats {nth} for you {human}'],
    tryhard: ['{human} move out of {ability}', '{human} please', '{human} you have to dodge that'],
    newbie: ['oh no {human}', 'I died to that too earlier lol'],
    clown: ['{human} has been yeeted', 'F for {human}'],
    leader: ['{human}, step out of {ability} early, its fine'],
  },
  wipe: {
    any: ['wipe', 'well', 'ok', 'run back'],
    kind: ['np, we got it next time', 'its ok, go again', 'close one'],
    salty: ['wow', 'cmon', 'what was that', 'this group...', 'nice'],
    tryhard: ['that was so avoidable', 'actually do mechanics this time', 'ok this is getting bad', 'who didnt kick'],
    newbie: ['sorry was that me', 'oh no'],
    leader: ['ok reset, kicks on casters this time', 'we are fine, go again'],
    clown: ['team building exercise', 'we died with honor', '*plays sad trombone*'],
    chill: ['lol', 'no worries, run back'],
  },
  kick_landed: {
    any: ['kicked', 'got it', 'interrupted {target}'],
    tryhard: ['kicked. you are welcome'],
    clown: ['denied', 'no spell for you'],
    leader: ['kicked {target}, next one is yours'],
  },
  kick_missed: {
    any: ['kick?', 'no kick?', 'kick pls'],
    kind: ['missed kick, np', 'try to kick {target}'],
    salty: ['KICK', 'nobody kicks', 'who is on kicks', 'kick {target} omg', 'we have 4 interrupts guys'],
    tryhard: ['KICK {target}', 'how is nobody kicking', 'thats on kick rotation'],
    leader: ['kick {target} next time pls'],
    newbie: ['was I supposed to kick that'],
    clown: ['the spell went brrr'],
  },
  interrupt_call: {
    any: ['kick {target}', 'kick the caster', 'interrupt {target}'],
    leader: ['kick {target}!', 'caster first, kick it'],
    tryhard: ['KICK {target}', 'kick it now'],
  },
  loot: {
    any: ['ooh shiny', 'chest!', 'nice'],
    goblin: ['MINE', 'ore ore ore', 'is that a node', 'dont mind me', 'free real estate'],
    clown: ['treasure!!'],
    tryhard: ['dont stop for loot'],
  },
  low_timer: {
    any: ['timer is close', 'hurry', 'we need to speed up'],
    tryhard: ['we are behind, no more stopping', 'pull bigger', 'stop dying and we make it'],
    chill: ['we might not make it, all good'],
    leader: ['tight timer, chain the next two'],
    newbie: ['are we going to make it?'],
    clown: ['the clock is ticking and so am I'],
  },
  timed: {
    any: ['gg', 'ty all', 'gg wp', 'timed!', 'nice run'],
    kind: ['ty all, great group', 'gg, that was fun'],
    salty: ['gg, could have been faster'],
    tryhard: ['ok gg', 'gg, close one', 'could have been a +3'],
    newbie: ['gg!! thank you so much', 'I learned a lot, ty'],
    clown: ['gg ez *dances*', 'another key for the wall'],
    goblin: ['gg, got 3 nodes'],
    leader: ['gg all, thanks for listening'],
    silent: ['gg'],
  },
  depleted: {
    any: ['gg', 'ty anyway', 'well', 'close'],
    kind: ['gg, next time', 'still fun, ty'],
    salty: ['depleted. nice', 'ok that was rough', 'gg I guess'],
    tryhard: ['depleted. great', 'that was a disaster', 'not grouping with this again'],
    newbie: ['sorry guys :(', 'was it my fault'],
    clown: ['we tried', 'the key is now decorative'],
    leader: ['gg, we will get it next time'],
  },
  afk_start: {
    any: ['brb', 'sec', '1 sec'],
    distracted: ['brb door', 'afk 1 min, dog', 'sec food is here', 'brb phone', 'one sec, kid needs something', 'brb doorbell', 'afk quick, kettle'],
    goblin: ['brb mail'],
    clown: ['brb, important cat business'],
    newbie: ['sorry brb!!'],
    chill: ['brb, grabbing a drink'],
  },
  afk_long: {
    any: ['sorry, longer than I thought', 'still here, sec'],
    distracted: ['sorry sorry, delivery guy', 'brb 2 min, something happened', 'ugh one more min'],
  },
  afk_back: {
    any: ['back', 'here', 'ok back', 'sorry back'],
    distracted: ['back, sorry!', 'ok im here', 'back, it was the neighbour'],
    clown: ['I have returned', 'back from the shadow realm'],
  },
  wander_start: {
    any: ['brb ore', 'sec, node', 'one sec, chest'],
    goblin: ['ooh herb', 'brb ore', 'node!', 'is that a chest', 'just gonna grab this', 'shiny, one sec'],
    clown: ['the shiny calls to me'],
  },
  wander_back: {
    any: ['ok coming', 'got it', 'back'],
    goblin: ['got it, worth', 'ok coming back', 'nice, +1 ore'],
  },
  waiting: {
    any: ['waiting on {victim}', 'wait for {victim}', '{victim}?'],
    tryhard: ['{victim} we are waiting', 'cmon {victim}', '{victim} hello?'],
    leader: ['hold up, waiting for {victim}', 'one sec, {victim} is away'],
    chill: ['np, waiting'],
  },
  tilt_1: {
    any: ['hm', 'ok...'],
    tryhard: ['this is getting rough', 'please play properly', 'is anyone watching the mechanics', 'pls focus'],
    salty: ['cmon guys', 'this is painful'],
    leader: ['lets refocus, we got this'],
  },
  tilt_2: {
    tryhard: ['last warning, one more death and I leave', 'I am about to leave', 'this is unplayable', 'why do I queue with randoms', 'unreal'],
    salty: ['done soon', 'lol this group', 'actually unplayable'],
    any: ['this is rough'],
  },
  rage_quit: {
    any: ['gg im out', 'im out', 'nope', 'gl'],
    tryhard: ['gg, not wasting my time', 'im out. learn the fight {victim}', 'bye', 'you guys are not ready for this key', 'leaving, cya'],
    salty: ['gg im out', 'waste of time, bye'],
    newbie: ['sorry, I cant do this, bye'],
  },
  someone_left: {
    any: ['rip key', 'wow', 'ok then', 'well', 'lol'],
    kind: ['thats a shame', 'we can still try'],
    salty: ['bye then', 'good riddance', 'lol bye'],
    clown: ['and then there were three', '*waves goodbye*'],
    newbie: ['did I do something wrong?'],
  },
  abandon: {
    any: ['gg', 'ty for the try', 'im out too', 'key is dead, gg'],
    kind: ['sorry all, gg', 'ty anyway'],
    salty: ['gg, what a waste'],
  },
  afk_kick: {
    any: ['{victim} is gone, gg', 'afk for 3 min, leaving', '{victim}??'],
    tryhard: ['afk in a key, unbelievable', 'not waiting for {victim} anymore'],
  },
  ambient: {
    any: ['nice', 'lol', 'ok', 'going well', 'smooth so far', 'good pace', 'love this dungeon', 'who has the next key'],
    kind: ['you all are doing great', 'good group', 'nice heals', 'tank is cracked'],
    salty: ['could be faster', 'we are slow', 'pls pull more', 'dps check incoming'],
    chill: ['this dungeon has nice music', 'anyone else hungry', 'good group', 'love the vibes here', 'I could do this all day', 'taking it easy, nice'],
    newbie: ['what does fortified do again', 'how do I see my dps', 'which key is dodge again lol', 'am I doing ok?', 'is my damage good', 'this is so fun', 'where do I stand on the boss'],
    goblin: ['anyone know ore prices', 'this zone has good herbs', 'I smell a chest', 'my bags are full of rocks', 'pls dont skip the chests'],
    distracted: ['sorry whats happening', 'wait where are we', 'sorry was reading chat', 'my tea went cold', 'which boss is next'],
    clown: ['*flexes*', 'what if the boss is just shy', '*dances*', 'fun fact: snakes cant blink', '*cheers*', 'I named that mob Steve', '*does a little spin*', 'who wants to see my mount'],
    tryhard: ['decent pace', 'we are ahead, keep it up', 'pull faster tank', 'dont waste cds on trash', 'chain pull, no breaks', 'dps is fine, deaths are not'],
    leader: ['good pace, keep it up', 'nice kicks everyone', 'next pull is the casters, kick them', 'save cds for the boss', 'we are on track'],
    silent: ['.', 'ok', 'ya'],
  },
  reply: {
    any: ['lol', 'ya', 'true', 'ok', 'haha', 'ye'],
    kind: ['you are doing great', 'np!', 'all good'],
    salty: ['sure', 'k', 'ok buddy'],
    clown: ['*nods wisely*', 'big if true'],
    newbie: ['oh ok!', 'thanks!'],
    tryhard: ['focus pls'],
  },
};

/** Ordinal suffix for {nth}. */
export function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  const d = n % 10;
  return `${n}${d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}`;
}

/** Fill {placeholders}; unknown ones stay readable (the key without braces). */
export function fillLine(text: string, vars: Readonly<Record<string, string | number | undefined>>): string {
  return text.replace(/\{(\w+)\}/g, (_m, k: string) => {
    const v = vars[k];
    return v === undefined || v === '' ? (k === 'victim' || k === 'human' ? 'you' : k === 'target' ? 'that' : k === 'ability' ? 'that' : k) : String(v);
  });
}
